<template>
    <b-modal id="fuel-transfer-confirm-modal" title="Transfer Onayı" ok-title="Onayla" ok-only @ok="handleOk" @hidden="resetModal" :ok-disabled="isConfirmDisabled" centered no-close-on-backdrop>
        <div v-if="loading" class="text-center p-2">
            <b-spinner variant="primary" label="Yükleniyor..."></b-spinner>
        </div>
        <div v-else>
            <div class="bg-light-primary rounded p-1 mb-2 border border-primary">
                <h6 class="font-weight-bolder text-primary mb-1 d-flex align-items-center border-bottom-primary pb-50">
                    <feather-icon icon="ActivityIcon" class="mr-50" />
                    Transfer Özeti
                </h6>

                <b-row class="font-small-3">
                    <b-col cols="12" class="mb-50 d-flex align-items-center">
                        <div class="text-muted mr-50 d-flex align-items-center" style="min-width: 90px">
                            <feather-icon icon="HashIcon" size="14" class="mr-50" />
                            <span>İşlem No:</span>
                        </div>
                        <span class="font-weight-bolder text-dark">{{ displayProcessNumber }}</span>
                    </b-col>

                    <b-col cols="12" class="mb-50 pb-50 d-flex align-items-center">
                        <div class="text-muted mr-50 d-flex align-items-center" style="min-width: 90px">
                            <feather-icon icon="ArrowDownCircleIcon" size="14" class="mr-50" />
                            <span>Hedef Şube:</span>
                        </div>
                        <span class="font-weight-bolder text-primary">{{ detail.TargetBranchName || detail.FuelStorageName || detail.FuelStorage || "-" }}</span>
                    </b-col>

                    <b-col cols="6" class="border-right pr-1">
                        <div class="mb-50 d-flex align-items-center">
                            <feather-icon icon="ArrowUpCircleIcon" size="14" class="mr-50 text-muted" />
                            <span class="text-muted mr-50">Kaynak:</span>
                            <span class="font-weight-bolder text-dark">{{ sourceDetail.FuelStorageName || "-" }}</span>
                        </div>
                        <div class="d-flex align-items-center">
                            <feather-icon icon="AnchorIcon" size="14" class="mr-50 text-muted" />
                            <span class="text-muted mr-50">Çıkış Kantar:</span>
                            <span class="font-weight-bolder text-dark">{{ sourceDetail.Tonnage ? formatNumber(sourceDetail.Tonnage) + " Ton" : "-" }}</span>
                        </div>
                    </b-col>

                    <b-col cols="6" class="pl-1">
                        <div class="mb-50 d-flex align-items-center text-primary">
                            <feather-icon icon="DropletIcon" size="14" class="mr-50 text-muted" />
                            <span class="mr-50 text-muted">Beklenen:</span>
                            <span class="font-weight-bolder">{{ formatNumber(expectedQuantity) }} lt</span>
                        </div>
                        <div class="d-flex align-items-center">
                            <feather-icon icon="FileTextIcon" size="14" class="mr-50 text-muted" />
                            <span class="text-muted mr-50">Kaynak Fişi:</span>
                            <b-button v-if="sourceDetail.File" variant="flat-primary" size="sm" class="p-0 border-0" @click="viewSourceFile" title="Fişi Görüntüle">
                                <span class="font-weight-bolder text-decoration-underline">Görüntüle</span>
                                <feather-icon icon="ExternalLinkIcon" size="12" class="ml-25" />
                            </b-button>
                            <span v-else class="text-muted">-</span>
                        </div>
                    </b-col>
                </b-row>
            </div>
            <b-form-group v-if="showStorageSelector" label="Hedef Depo" class="is-required">
                <v-select-list
                    v-model="form.FuelStorageId"
                    :options="availableStorages"
                    label="DisplayName"
                    :reduce="(s) => s.Id"
                    placeholder="Depo seçiniz..."
                />
            </b-form-group>
            <div class="row">
                <b-form-group label="Kantar Ölçümü [ton]" class="col-6 is-required">
                    <v-currency-input :value="form.Tonnage" @update-value="handleTonnageUpdate" placeholder="Tonaj giriniz..." :input-class="'font-weight-bold'" />
                </b-form-group>

                <b-form-group label="Yoğunluk [kg/lt]" label-class="font-weight-bold" class="col-6 is-required">
                    <b-form-input v-model.number="form.Density" type="number" step="0.0001" placeholder="Yoğunluk giriniz..." @input="calculateQuantity" />
                </b-form-group>
            </div>
            <b-form-group label="Miktar [lt]" class="is-required">
                <v-currency-input :value="form.ConfirmQuantity" disabled input-class="bg-light font-weight-bolder text-primary" placeholder="0,00" />
                <small class="text-muted d-block mt-25 font-small-2">
                    <feather-icon icon="InfoIcon" size="10" class="mr-25" />
                    Formül: (Ton x 1000) / Yoğunluk ({{ form.Density }})
                </small>
                <small v-if="quantityMismatchMessage" class="text-danger d-block mt-25 font-small-2">
                    <feather-icon icon="AlertCircleIcon" size="10" class="mr-25" />
                    {{ quantityMismatchMessage }}
                </small>
            </b-form-group>

            <b-form-group label="Kantar Fişi" class="is-required">
                <input type="file" ref="fileInput" class="d-none" @change="handleFileUpload" accept=".pdf,.jpg,.jpeg,.png" />

                <div class="d-flex align-items-center">
                    <b-button v-if="!form.File" variant="outline-primary" size="sm" class="w-100 d-flex align-items-center justify-content-center" style="height: 38px" @click="$refs.fileInput.click()">
                        <feather-icon icon="UploadCloudIcon" class="mr-50" />
                        <span class="text-truncate">Fiş Yükle</span>
                    </b-button>

                    <div v-else class="d-flex align-items-center justify-content-between w-100 border rounded px-1" style="height: 38px" :class="form.File ? 'border-success bg-light-success' : 'border-primary bg-light'">
                        <div class="d-flex align-items-center overflow-hidden h-100 py-25">
                            <div v-if="previewUrl" class="mr-50 border rounded bg-white" style="height: 100%; width: 30px; min-width: 30px; background-size: cover; background-position: center" :style="{ backgroundImage: 'url(' + previewUrl + ')' }"></div>
                            <feather-icon v-else icon="FileTextIcon" size="20" class="mr-50" :class="form.File ? 'text-success' : 'text-primary'" />

                            <span class="text-truncate font-small-3 font-weight-bold" :class="form.File ? 'text-success' : 'text-primary'" :title="form.File.name">
                                {{ form.File.name }}
                            </span>
                        </div>

                        <div class="cursor-pointer p-50 d-flex align-items-center justify-content-center text-danger hover-danger" @click="removeFile">
                            <feather-icon icon="XIcon" size="18" />
                        </div>
                    </div>
                </div>
            </b-form-group>
        </div>
    </b-modal>
</template>

<script>
import { BModal, BFormGroup, BAlert, BSpinner, BButton, BRow, BCol, VBTooltip, BFormInput } from "bootstrap-vue";
import VCurrencyInput from "@/components/VCurrencyInput.vue";
import FuelStorageApi from "@/api/fuel-storage";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default {
    name: "FuelTransferConfirmModal",
    components: {
        BModal,
        BFormGroup,
        BAlert,
        BSpinner,
        BButton,
        BRow,
        BCol,
        BFormInput,
        VCurrencyInput,
    },
    directives: {
        "b-tooltip": VBTooltip,
    },
    data() {
        return {
            loading: false,
            detail: {},
            sourceDetail: {},
            processNumber: "",
            currentFuelTypeName: null,
            DENSITY_DIESEL: 0.8314,
            DENSITY_ADBLUE: 1.09,
            FUEL_TYPE_ID_MOTORIN: "3a82d544-bfda-4995-ad74-29982d9606df",
            FUEL_TYPE_ID_ADBLUE: "b8694977-e4fc-4c90-b02f-d7bc04caa146",
            previewUrl: null,
            availableStorages: [],
            showStorageSelector: false,
            form: {
                Tonnage: null,
                Density: 0.8314,
                ConfirmQuantity: null,
                File: null,
                FuelStorageId: null,
            },
        };
    },
    computed: {
        displayProcessNumber() {
            if (this.processNumber && this.processNumber !== "-") return this.processNumber;
            if (this.detail && this.detail.ProcessNumber) return this.detail.ProcessNumber;
            return "-";
        },
        expectedQuantity() {
            return this.normalizeNumber(this.detail.Quantity ?? this.detail.IncomingQuantity);
        },
        quantityMismatchMessage() {
            const expectedQuantity = this.roundToTwo(this.expectedQuantity);
            const confirmQuantity = this.roundToTwo(this.form.ConfirmQuantity);

            if (expectedQuantity === null || confirmQuantity === null || confirmQuantity <= 0) return "";
            if (confirmQuantity === expectedQuantity) return "";

            const direction = confirmQuantity > expectedQuantity ? "fazla" : "eksik";
            return `Girilen miktar beklenen transfer miktarı ile eşleşmiyor. Beklenen: ${this.formatNumber(expectedQuantity)} lt, Girilen: ${this.formatNumber(confirmQuantity)} lt. ${direction.charAt(0).toUpperCase() + direction.slice(1)} giriş kabul edilmez.`;
        },
        isConfirmDisabled() {
            return this.loading || !this.form.Tonnage || this.form.Tonnage <= 0 || !this.form.File || !!this.quantityMismatchMessage || this.expectedQuantity === null || !this.form.ConfirmQuantity || this.form.ConfirmQuantity <= 0 || (this.showStorageSelector && !this.form.FuelStorageId);
        },
    },
    methods: {
        normalizeNumber(value) {
            if (value === null || value === undefined || value === "") return null;

            if (typeof value === "string") {
                const cleanVal = value.replace(/\./g, "").replace(",", ".");
                const parsed = parseFloat(cleanVal);
                return isNaN(parsed) ? null : parsed;
            }

            const parsed = Number(value);
            return isNaN(parsed) ? null : parsed;
        },
        roundToTwo(value) {
            const parsed = this.normalizeNumber(value);
            if (parsed === null) return null;
            return Number(parsed.toFixed(2));
        },
        formatNumber(value) {
            if (value === null || value === undefined) return "-";

            let num = value;
            if (typeof value === "string") {
                const cleanVal = value.replace(/\./g, "").replace(",", ".");
                num = parseFloat(cleanVal);
            }

            if (isNaN(num)) return "-";

            return new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
        },
        open(detail, sourceDetail, processNumber, fuelTypeName, targetBranchId) {
            this.detail = detail || {};
            this.sourceDetail = sourceDetail || {};
            this.processNumber = processNumber || "-";
            this.currentFuelTypeName = fuelTypeName || null;

            this.resetForm();

            if (fuelTypeName === "AdBlue") {
                this.form.Density = this.DENSITY_ADBLUE;
            } else {
                this.form.Density = this.DENSITY_DIESEL;
            }

            if (targetBranchId) {
                this.showStorageSelector = true;
                this.loadAvailableStorages(targetBranchId, fuelTypeName);
            } else {
                this.showStorageSelector = false;
                this.availableStorages = [];
            }

            this.$bvModal.show("fuel-transfer-confirm-modal");
        },
        resolveFuelTypeIdFromName(fuelTypeName) {
            if (!fuelTypeName) return null;
            const normalized = String(fuelTypeName).trim().toLowerCase();
            if (normalized === "adblue") return this.FUEL_TYPE_ID_ADBLUE;
            if (normalized === "motorin") return this.FUEL_TYPE_ID_MOTORIN;
            return null;
        },
        loadAvailableStorages(branchId, fuelTypeName) {
            this.availableStorages = [];
            const expectedFuelTypeId = this.resolveFuelTypeIdFromName(fuelTypeName);
            FuelStorageApi.FuelStorage.List().then((res) => {
                if (res.data && res.data.Success) {
                    const allStorages = res.data.Data || [];
                    this.availableStorages = allStorages
                        .filter((s) => s.FuelStorageOwningBranchId === branchId)
                        // Yakıt tipi bilinen bir transferse (Motorin/AdBlue) hedef depo
                        // listesi de bu yakıt tipiyle eşleşen depolara daraltılır;
                        // örn. Motorin transferinde Adblue deposu listelenmez.
                        .filter((s) => !expectedFuelTypeId || s.FuelStorageFuelTypeId === expectedFuelTypeId)
                        .map((s) => ({
                            ...s,
                            DisplayName: `${s.Name} [Kapasite: ${(s.Capacity || 0).toLocaleString("tr-TR")} lt, Boş: ${((s.Capacity || 0) - (s.FuelQuantity || 0)).toLocaleString("tr-TR")} lt]`,
                        }));
                }
            }).catch((err) => {
                console.error("Depo listesi yüklenemedi:", err);
                this.availableStorages = [];
            });
        },
        resetForm() {
            this.form.Tonnage = null;
            this.form.ConfirmQuantity = null;
            this.form.File = null;
            this.form.FuelStorageId = null;
            this.previewUrl = null;
        },
        resetModal() {
            this.detail = {};
            this.sourceDetail = {};
            this.processNumber = "";
            this.currentFuelTypeName = null;
            this.availableStorages = [];
            this.showStorageSelector = false;
            this.resetForm();
        },
        viewSourceFile() {
            if (this.sourceDetail && this.sourceDetail.File) {
                window.open(this.sourceDetail.File, "_blank");
            }
        },
        handleTonnageUpdate(val) {
            this.form.Tonnage = val;
            this.calculateQuantity();
        },
        calculateQuantity() {
            const ton = this.form.Tonnage;
            const density = this.form.Density;

            if (ton && ton > 0 && density && density > 0) {
                const result = (ton * 1000) / density;
                this.form.ConfirmQuantity = this.roundToTwo(result);
            } else {
                this.form.ConfirmQuantity = null;
            }
        },
        handleFileUpload(event) {
            const file = event.target.files[0];
            if (file) {
                if (file.size > 5 * 1024 * 1024) {
                    this.$toast({
                        component: ToastificationContent,
                        props: { title: "Uyarı", icon: "AlertTriangleIcon", variant: "warning", text: "Dosya boyutu 5MB'dan büyük olamaz." },
                    });
                    return;
                }
                this.form.File = file;

                if (file.type.startsWith("image/")) {
                    const reader = new FileReader();
                    reader.onload = (e) => {
                        this.previewUrl = e.target.result;
                    };
                    reader.readAsDataURL(file);
                } else {
                    this.previewUrl = null;
                }
            }
            event.target.value = null;
        },
        removeFile() {
            this.form.File = null;
            this.previewUrl = null;
        },
        handleOk(bvModalEvt) {
            bvModalEvt.preventDefault();
            this.submit();
        },
        submit() {
            if (!this.form.Tonnage || this.form.Tonnage <= 0) {
                this.$toast({ component: ToastificationContent, props: { title: "Hata", icon: "AlertCircleIcon", variant: "danger", text: "Lütfen geçerli bir tonaj giriniz." } });
                return;
            }
            if (!this.form.ConfirmQuantity || this.form.ConfirmQuantity <= 0) {
                this.$toast({ component: ToastificationContent, props: { title: "Hata", icon: "AlertCircleIcon", variant: "danger", text: "Lütfen geçerli bir miktar oluşturunuz." } });
                return;
            }

            const expectedQuantity = this.roundToTwo(this.expectedQuantity);
            const confirmQuantity = this.roundToTwo(this.form.ConfirmQuantity);

            if (expectedQuantity === null) {
                this.$toast({ component: ToastificationContent, props: { title: "Hata", icon: "AlertCircleIcon", variant: "danger", text: "Beklenen transfer miktarı bulunamadı." } });
                return;
            }

            if (confirmQuantity !== expectedQuantity) {
                const direction = confirmQuantity > expectedQuantity ? "Fazla" : "Eksik";
                this.$toast({
                    component: ToastificationContent,
                    props: {
                        title: "Hata",
                        icon: "AlertCircleIcon",
                        variant: "danger",
                        text: `Girilen miktar beklenen transfer miktarı ile eşleşmiyor. Beklenen: ${this.formatNumber(expectedQuantity)} lt, Girilen: ${this.formatNumber(confirmQuantity)} lt. ${direction} giriş kabul edilmez.`,
                    },
                });
                return;
            }

            if (!this.form.File) {
                this.$toast({ component: ToastificationContent, props: { title: "Hata", icon: "AlertCircleIcon", variant: "danger", text: "Lütfen kantar fişi yükleyiniz." } });
                return;
            }

            this.loading = true;
            this.$emit("confirm", {
                id: this.detail.Id,
                ...this.form,
            });
        },
        hide() {
            this.$bvModal.hide("fuel-transfer-confirm-modal");
            this.loading = false;
        },
    },
};
</script>
<style scoped>
.is-required >>> .col-form-label::after,
.is-required /deep/ .col-form-label::after {
    content: " *";
    color: #dc3545;
    font-weight: 700;
}
</style>
